import { useState, useMemo, useCallback, useEffect } from 'react';
import { useApp } from '@context/AppContext';
import { useSquiggle } from './useSquiggle';
import { Star } from 'lucide-react';

/**
 * Voting phase — sequential card-by-card review of completed transformations.
 * Adaptive: 3-star taps for 6+ players, 1-10 slider for 3-5.
 * Players skip pairs where they were the transformer.
 */
export default function SquiggleVoting() {
  const { state } = useApp();
  const { game, user, room } = state;
  const { submitVoteForPair, finishVoting, votingMode, calculateResults } = useSquiggle();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sliderValue, setSliderValue] = useState(5);
  const [sliderMoved, setSliderMoved] = useState(false);
  const [starValue, setStarValue] = useState(0);
  const [allVotesDone, setAllVotesDone] = useState(false);

  // Build voting order — randomised, skip pairs where user was the transformer
  const votingOrder = useMemo(() => {
    const pairs = game.pairs || [];
    const snapshots = game.snapshots || {};

    const votable = pairs
      .map((pair, idx) => ({ ...pair, idx, snapshot: snapshots[idx] }))
      .filter(entry => entry.snapshot?.completed && entry.transformer !== user.id);

    // Shuffle
    for (let i = votable.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [votable[i], votable[j]] = [votable[j], votable[i]];
    }

    return votable;
  }, [game.pairs, game.snapshots, user.id]);

  const currentEntry = votingOrder[currentIndex];
  const totalToVote = votingOrder.length;

  // Submit vote and advance
  const handleVote = useCallback(async () => {
    if (!currentEntry) return;
    const score = votingMode === 'stars' ? starValue : sliderValue;
    await submitVoteForPair(currentEntry.idx, score);

    // Reset for next
    setSliderValue(5);
    setSliderMoved(false);
    setStarValue(0);

    const nextIndex = currentIndex + 1;
    if (nextIndex >= votingOrder.length) {
      await finishVoting();
      setAllVotesDone(true);
    } else {
      setCurrentIndex(nextIndex);
    }
  }, [currentEntry, votingMode, starValue, sliderValue,
      submitVoteForPair, currentIndex, votingOrder.length, finishVoting]);

  // Check if all players have finished voting — host advances
  const votes = game.votes || {};
  const memberCount = Object.keys(room.members || {}).length;
  const votersComplete = Object.values(votes).filter(v => v.submittedAt).length;

  // No one to vote on (e.g. solo) — skip to results
  useEffect(() => {
    if (user.isHost && totalToVote === 0) {
      const timer = setTimeout(() => calculateResults(), 2500);
      return () => clearTimeout(timer);
    }
  }, [user.isHost, totalToVote, calculateResults]);

  // All votes in — host calculates results
  useEffect(() => {
    if (user.isHost && totalToVote > 0 && votersComplete >= memberCount) {
      calculateResults();
    }
  }, [user.isHost, totalToVote, votersComplete, memberCount, calculateResults]);

  // Solo: brief message
  if (totalToVote === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm mx-4">
          <p className="text-lg font-semibold text-charcoal font-display mb-2">Solo round!</p>
          <p className="text-sm text-slate-400 font-display">
            No one else to vote on — heading straight to results...
          </p>
        </div>
      </div>
    );
  }

  if (!currentEntry) return null;

  // Waiting screen after all votes submitted
  if (allVotesDone) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm mx-4">
          <p className="text-lg font-semibold text-charcoal font-display mb-2">Votes submitted!</p>
          <p className="text-sm text-slate-400 font-display">
            Waiting for everyone to finish voting...
          </p>
          <p className="text-xs text-slate-300 font-mono mt-4">
            {votersComplete} / {memberCount} done
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Progress */}
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <p className="text-xs text-slate-400 font-display">
            {currentIndex + 1} of {totalToVote}
          </p>
          <p className="text-xs text-slate-400 font-display">
            {currentEntry.snapshot?.squigglerNickname} → {currentEntry.snapshot?.transformerNickname}
          </p>
        </div>

        {/* Completed image */}
        <div className="relative">
          <img
            src={currentEntry.snapshot?.completed}
            alt="Transformation"
            className="w-full aspect-[4/3] object-cover bg-slate-50"
          />
        </div>

        {/* Voting controls */}
        <div className="p-5">
          <p className="text-sm font-semibold text-charcoal font-display mb-3 text-center">
            How good is this transformation?
          </p>

          {votingMode === 'stars' ? (
            /* 3-star tap voting (6+ players) */
            <div className="flex justify-center gap-3 mb-4">
              {[1, 2, 3].map((star) => (
                <button
                  key={star}
                  onClick={() => setStarValue(star)}
                  className="p-2 transition-transform hover:scale-110"
                >
                  <Star
                    className={`w-10 h-10 transition-colors ${
                      star <= starValue
                        ? 'text-amber fill-amber'
                        : 'text-slate-200'
                    }`}
                  />
                </button>
              ))}
            </div>
          ) : (
            /* 1-10 slider voting (3-5 players) */
            <div className="mb-4">
              <input
                type="range"
                min={1}
                max={10}
                value={sliderValue}
                onChange={(e) => {
                  setSliderValue(Number(e.target.value));
                  setSliderMoved(true);
                }}
                className="w-full accent-amber"
              />
              <div className="flex justify-between text-xs text-slate-300 font-mono px-1">
                <span>1</span>
                <span className="text-lg font-bold text-amber">{sliderValue}</span>
                <span>10</span>
              </div>
            </div>
          )}

          <button
            onClick={handleVote}
            disabled={votingMode === 'stars' ? starValue === 0 : !sliderMoved}
            className="w-full px-5 py-2.5 rounded-xl bg-amber text-white text-sm font-semibold font-display hover:bg-amber/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {votingMode === 'stars' ? `Rate: ${starValue} star${starValue !== 1 ? 's' : ''}` : `Rate: ${sliderValue}/10`}
          </button>
        </div>
      </div>
    </div>
  );
}
